import { useEffect, useState } from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { Textarea } from '../../../../components/inputs/Textarea';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { RichTextDocument } from '../../../../components/RichTextDocument';
import { Skeleton } from '../../../../components/Skeleton';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { approvalsApi } from '../../../approvals/approvalsApi';
import { DECISION_FAILED } from '../../../approvals/approvalTypes';
import { surveyTrainingRecommendationsApi } from '../surveyApi';
import { TRAINING_RECOMMENDATION_STATUS, type SurveyTrainingRecommendationDetail } from '../surveyTypes';

const ID = 'training-recommendation-review';

/** The window's one piece of decision state: reading, or deciding one way. */
export type TrainingRecommendationReviewStage = 'review' | 'approve' | 'decline';

type Stage = TrainingRecommendationReviewStage;

/**
 * Training Recommendation › Review — the recommendation read as a document, and the decision on
 * it (product call, 13 Oct 2026). The development plan's own Review, for a recommendation.
 *
 * It opens on the PREVIEW: what is recommended, and on which plan. An approver the request is
 * waiting on is offered **Approve** and **Decline**; either turns the same window into the
 * decision's own form — the consequence, the remarks and the confirm tick — rather than stacking
 * a second dialog.
 *
 * **The decision is the approval engine's**, on `approvals.decide`, made against the
 * recommendation's own request. Decline is the engine's rejection, so its remarks are REQUIRED and
 * are what Learning & Development is told; an approval's remarks are OPTIONAL. The status then
 * moves in the engine's callback — nothing here marks a recommendation approved.
 *
 * **The buttons follow the ENGINE's answer, not the key alone**: holding `approvals.decide` does
 * not make a reader this request's approver. Without both, the window is the preview and a line
 * saying why there is nothing to decide. NO Autogenerate — a decision's remarks are a judgement.
 */
export function SurveyTrainingRecommendationReviewModal({
  open,
  recommendationId,
  title,
  onClose,
  onDecided,
  initialStage = 'review',
}: {
  open: boolean;
  recommendationId: string;
  /** The recommendation's title, for the window's heading while it loads. */
  title: string;
  onClose: () => void;
  onDecided: () => void;
  /** The stage it OPENS on — for a page that already shows the preview, so its buttons land on the form. */
  initialStage?: TrainingRecommendationReviewStage;
}) {
  const { hasPermission } = useAuth();
  const toast = useToast();

  const [detail, setDetail] = useState<SurveyTrainingRecommendationDetail | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [stage, setStage] = useState<Stage>(initialStage);
  const [remarks, setRemarks] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    surveyTrainingRecommendationsApi
      .get(recommendationId, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setDetail(next);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setLoadError(caught instanceof ApiError ? caught : new ApiError('Could not load this training recommendation.', 0));
      });

    return () => controller.abort();
  }, [recommendationId]);

  const approval = detail?.approval ?? null;
  const waiting = detail != null && detail.recommendation.status === TRAINING_RECOMMENDATION_STATUS.Submitted;
  // `=== true`: the flag came off the wire, where an absent approval leaves it undefined.
  const canDecide = waiting && approval?.canDecide === true && hasPermission('approvals.decide');

  const deciding = stage !== 'review';
  const declining = stage === 'decline';
  const remarksValid = !declining || remarks.trim().length > 0;
  const canSave = deciding && canDecide && remarksValid && confirmed && !saving;

  function begin(next: Stage) {
    setStage(next);
    setRemarks('');
    setConfirmed(false);
    setError(null);
  }

  async function save() {
    if (!canSave || approval == null) return;

    setSaving(true);
    setError(null);

    try {
      if (declining) {
        await approvalsApi.reject(approval.requestId, remarks.trim());
      } else {
        await approvalsApi.approve(approval.requestId, remarks.trim() || null);
      }

      // The failure does NOT toast: the form stays open, and the strip and the field are where the reader is.
      toast.success(`${declining ? 'Declined' : 'Approved'} "${title}".`);
      onDecided();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError(DECISION_FAILED, 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={deciding ? `${declining ? 'Decline' : 'Approve'} "${title}"` : title}
      size="xl"
      id={ID}
      // The tick appears only while deciding — the one write here.
      confirm={deciding ? { id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving } : undefined}
      footer={
        deciding ? (
          <>
            <Button id={`${ID}-back`} variant="secondary" onClick={() => begin('review')} disabled={saving}>
              Back
            </Button>
            <Button
              id={`${ID}-submit`}
              // Never red: it is the form's one action either way.
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </>
        ) : (
          <>
            <Button id={`${ID}-close`} variant="secondary" onClick={onClose}>
              Close
            </Button>
            {canDecide ? (
              <>
                <Button id={`${ID}-decline`} variant="secondary" onClick={() => begin('decline')}>
                  Decline
                </Button>
                <Button id={`${ID}-approve`} variant="primary" onClick={() => begin('approve')}>
                  Approve
                </Button>
              </>
            ) : null}
          </>
        )
      }
    >
      {loadError ? (
        <ErrorState message={loadError.message} correlationId={loadError.correlationId} />
      ) : detail == null ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : deciding ? (
        <div className="flex flex-col gap-4">
          {error ? (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-xs text-red-700">{error.message}</p>
            </div>
          ) : null}

          {/* THE CONSEQUENCE BEFORE THE TICK, and it differs per decision. */}
          <p className="text-xs text-slate-600">
            {declining
              ? 'This ends the approval request. The recommendation goes back to Learning & Development, who can revise it and submit it again.'
              : 'This signs the recommendation as its approver. Once the approval is complete it is final and can no longer be changed.'}
          </p>

          <Textarea
            id={`${ID}-${declining ? 'reason' : 'remarks'}`}
            label="Remarks"
            required={declining}
            description={
              declining
                ? 'What has to change. This is what Learning & Development is told, so say what about the training rather than that it was declined.'
                : 'Optional. Kept with your approval.'
            }
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            error={
              declining
                ? remarks.trim()
                  ? error?.fieldError('reason')
                  : 'Say why it is being declined.'
                : error?.fieldError('remarks')
            }
            disabled={saving}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Why there is nothing to decide — said, rather than left as two missing buttons. */}
          {!canDecide ? (
            <p className="text-xs text-slate-600">
              {waiting
                ? 'This training recommendation is waiting for approval, but not on your decision.'
                : 'This training recommendation is not waiting for approval.'}
            </p>
          ) : null}

          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Title', value: detail.recommendation.title },
              { label: 'Development plan', value: detail.planTitle },
              { label: 'Survey', value: detail.surveyTitle },
            ]}
          />

          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-medium text-slate-800">Recommendation</h3>
            {/* The one renderer of stored markup — safe only because the server sanitised it on write. */}
            <RichTextDocument html={detail.details ?? null} />
          </section>
        </div>
      )}
    </Modal>
  );
}
